import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  IconButton,
  Switch,
  TextField,
  Typography,
  Chip,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  Delete,
  Shield,
  Link as LinkIcon,
  Star,
  InfoOutlined,
  Settings as SettingsIcon,
  Public,
  ArrowBack,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TaskLayout from "./components/TaskLayout";
import { BaseScrollableContainer } from "@/Components/scrollable/BaseScrollableContainer";
import {
  useCreateCompanyConfigMutation,
  useGetConfigDetailsQuery,
  useUpdateCompanyConfigMutation,
} from "@/features/bughurd/api";
import { toast } from "react-toastify";

interface WhitelistItem {
  id: string;
  type: "EXACT" | "WILDCARD";
  url: string;
  description?: string;
}

type UrlType = "exact" | "wildcard";

export default function Settings() {
  const { t } = useTranslation(["bugherd"]);
  const navigate = useNavigate();
  const [createCompanyConfig, { isLoading: isCreating }] =
    useCreateCompanyConfigMutation();
  const [updateCompanyConfig, { isLoading: isUpdating }] =
    useUpdateCompanyConfigMutation();
  const [urlInput, setUrlInput] = useState("");
  const [currentUrlType, setCurrentUrlType] = useState<UrlType>("exact");
  const [whitelistedUrls, setWhitelistedUrls] = useState<WhitelistItem[]>([]);
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [compactView, setCompactView] = useState(false);
  const [existingConfigId, setExistingConfigId] = useState<string | null>(null);
  const [deletingUrlId, setDeletingUrlId] = useState<string | null>(null);
  const [savingTheme, setSavingTheme] = useState(false);
  const [savingEmailNotif, setSavingEmailNotif] = useState(false);
  const [urlFilter, setUrlFilter] = useState<"ALL" | "EXACT" | "WILDCARD">(
    "ALL",
  );
  const { data: configData, isLoading: isLoadingConfig } =
    useGetConfigDetailsQuery({});

  // Populate form fields with existing SNIFFER config data
  useEffect(() => {
    if (configData?.data?.decryptedData) {
      // Find the SNIFFER config from decryptedData
      const snifferConfig = configData?.data?.decryptedData?.find(
        (config: any) => config.type === "SNIFFER",
      );

      if (snifferConfig) {
        setExistingConfigId(snifferConfig?._id || null);

        if (snifferConfig?.data) {
          if (snifferConfig?.data?.whitelistedUrls) {
            const urls = snifferConfig?.data?.whitelistedUrls?.map(
              (item: any, index: number) => {
                const domain = item.url.replace("*.", "");
                const description =
                  item.type === "WILDCARD"
                    ? `Matches: sub.${domain}, dev.sub.${domain}, etc.`
                    : "";

                return {
                  id: `${Date.now()}-${index}`,
                  type: item.type,
                  url: item.url,
                  description,
                };
              },
            );
            setWhitelistedUrls(urls);
          }

          if (snifferConfig?.data?.generalSettings) {
            const { theme, emailNotifications, compactView } =
              snifferConfig?.data?.generalSettings;

            if (theme) setTheme(theme);
            if (emailNotifications !== undefined)
              setEmailNotifications(emailNotifications);
            if (compactView !== undefined) setCompactView(compactView);
          }
        }
      }
    }
  }, [configData]);

  // Warn user about unsaved changes when leaving page

  const addUrl = async () => {
    const value = urlInput.trim();
    if (!value) return;

    // Auto-detect type based on pattern
    let type: "EXACT" | "WILDCARD" = currentUrlType.toUpperCase() as
      | "EXACT"
      | "WILDCARD";
    if (value.startsWith("*.")) {
      type = "WILDCARD";
    }

    const domain = value?.replace("*.", "");
    const description =
      type === "WILDCARD"
        ? `Matches: sub.${domain}, dev.sub.${domain}, etc.`
        : "";

    const newUrls = [
      ...whitelistedUrls,
      {
        id: Date.now().toString(),
        type: type,
        url: value,
        description,
      },
    ];

    setWhitelistedUrls(newUrls);
    setUrlInput("");

    // Auto-save immediately
    await handleSaveSettings(newUrls);
  };

  const removeUrl = async (id: string) => {
    setDeletingUrlId(id);
    const newUrls = whitelistedUrls?.filter((item) => item.id !== id);
    setWhitelistedUrls(newUrls);

    // Auto-save immediately
    await handleSaveSettings(newUrls);
    setDeletingUrlId(null);
  };

  const handleUrlTypeChange = (type: UrlType) => {
    setCurrentUrlType(type);
  };

  const handleInputChange = (value: string) => {
    setUrlInput(value);
    // Auto-detect wildcard pattern
    if (value.startsWith("*.") && currentUrlType !== "wildcard") {
      setCurrentUrlType("wildcard");
    }
  };

  const getFilteredUrls = () => {
    if (urlFilter === "ALL") return whitelistedUrls;
    return whitelistedUrls.filter((url) => url.type === urlFilter);
  };

  const filteredUrls = getFilteredUrls();

  const getPlaceholder = () => {
    return currentUrlType === "exact"
      ? t("Enter complete URL (e.g., https://api.walmart.com/v1/products)")
      : t("Enter wildcard pattern (e.g., *.walmart.com)");
  };

  const handleSaveSettings = async (
    urls?: WhitelistItem[],
    themeValue?: "light" | "dark",
    emailNotif?: boolean,
    compactViewValue?: boolean,
  ) => {
    const payload = {
      type: "SNIFFER",
      data: {
        whitelistedUrls: (urls || whitelistedUrls)?.map((item) => ({
          type: item.type,
          url: item.url,
        })),
        generalSettings: {
          theme: themeValue ?? theme,
          emailNotifications: emailNotif ?? emailNotifications,
          compactView: compactViewValue ?? compactView,
        },
      },
    };

    try {
      let response;
      if (existingConfigId) {
        // Update existing config
        response = await updateCompanyConfig({
          payload,
          id: existingConfigId,
        }).unwrap();
      } else {
        // Create new config
        response = await createCompanyConfig(payload).unwrap();

        // Store the new config ID for future updates
        if (response?.data?._id) {
          setExistingConfigId(response.data._id);
        }
      }

      toast.success(t("Settings saved successfully"));
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error(t("Failed to save settings. Please try again."));
    }
  };

  // Show loader while fetching config data
  if (isLoadingConfig) {
    return (
      <TaskLayout>
        <BaseScrollableContainer>
          <Container
            maxWidth={false}
            sx={{
              py: 5,
              px: 4,
              maxWidth: "900px",
              margin: "0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "60vh",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress
                size={48}
                sx={{
                  color: (theme) => theme.palette.primary.main,
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  fontSize: "15px",
                }}
              >
                {t("Loading settings...")}
              </Typography>
            </Box>
          </Container>
        </BaseScrollableContainer>
      </TaskLayout>
    );
  }

  return (
    <TaskLayout>
      <BaseScrollableContainer>
        <Container
          maxWidth={false}
          sx={{
            py: 3,
            px: 3,
            maxWidth: "900px",
            margin: "0 auto",
          }}
        >
          {/* Page Header */}
          <Box mb={3}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/defect-tracker/projects")}
              sx={{
                mb: 2,
                color: (theme) => theme.palette.text.secondary,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                "&:hover": {
                  color: (theme) => theme.palette.primary.main,
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              {t("Back to Projects")}
            </Button>
            <Typography
              variant="h3"
              fontWeight={700}
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? theme.palette.text.primary
                    : theme.palette.text.primary,
                mb: 1,
              }}
            >
              {t("Settings")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: (theme) => theme.palette.text.secondary,
                fontSize: "15px",
              }}
            >
              {t("Configure your whitelist URLs and application preferences")}
            </Typography>
          </Box>

          {/* Whitelist URLs Section */}
          <Box
            sx={{
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.02)",
              backdropFilter: "blur(20px)",
              border: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)"
                }`,
              borderRadius: "20px",
              p: 2.5,
              mb: 2.5,
            }}
          >
            {/* Section Header */}
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              mb={2.5}
              pb={2}
              sx={{
                borderBottom: (theme) =>
                  `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)"
                  }`,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  background: (theme) => `${theme.palette.primary.main}26`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Shield
                  sx={{
                    fontSize: 22,
                    color: (theme) => theme.palette.primary.main,
                  }}
                />
              </Box>
              <Box flex={1}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    mb: 0.5,
                  }}
                >
                  {t("Whitelist URLs")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: "14px",
                  }}
                >
                  {t("Add URLs that should be allowed through the filter")}
                </Typography>
              </Box>
            </Box>

            {/* URL Type Tabs */}
            <Box display="flex" gap={1.5} mb={2.5}>
              {/* Exact URL Tab */}
              <Box
                onClick={() => handleUrlTypeChange("exact")}
                sx={{
                  flex: 1,
                  background: (theme) =>
                    currentUrlType === "exact"
                      ? `${theme.palette.primary.main}1A`
                      : theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.03)"
                        : "rgba(0, 0, 0, 0.02)",
                  border: (theme) =>
                    currentUrlType === "exact"
                      ? `1px solid ${theme.palette.primary.main}`
                      : theme.palette.mode === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(0, 0, 0, 0.1)",
                  borderRadius: "12px",
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: (theme) =>
                      currentUrlType === "exact"
                        ? `${theme.palette.primary.main}1A`
                        : theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.06)"
                          : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={1.25} mb={0.75}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      background: (theme) =>
                        currentUrlType === "exact"
                          ? theme.palette.primary.main
                          : theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: (theme) =>
                        currentUrlType === "exact"
                          ? theme.palette.common.white
                          : theme.palette.text.secondary,
                      transition: "all 0.3s ease",
                    }}
                  >
                    <LinkIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      color: (theme) => theme.palette.text.primary,
                      fontSize: "14px",
                    }}
                  >
                    {t("Exact URL")}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: "12px",
                    lineHeight: 1.5,
                    mb: 1.25,
                  }}
                >
                  {t("Match a specific, complete URL exactly as entered")}
                </Typography>
                <Box
                  sx={{
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(0, 0, 0, 0.3)"
                        : "rgba(0, 0, 0, 0.05)",
                    borderRadius: "8px",
                    px: 1.25,
                    py: 1,
                    fontFamily: "monospace",
                    fontSize: "11px",
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.success.light
                        : theme.palette.success.dark,
                  }}
                >
                  https://api.walmart.com/v1/products
                </Box>
              </Box>

              {/* Wildcard Tab */}
              <Box
                onClick={() => handleUrlTypeChange("wildcard")}
                sx={{
                  flex: 1,
                  background: (theme) =>
                    currentUrlType === "wildcard"
                      ? `${theme.palette.primary.main}1A`
                      : theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.03)"
                        : "rgba(0, 0, 0, 0.02)",
                  border: (theme) =>
                    currentUrlType === "wildcard"
                      ? `1px solid ${theme.palette.primary.main}`
                      : theme.palette.mode === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(0, 0, 0, 0.1)",
                  borderRadius: "12px",
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: (theme) =>
                      currentUrlType === "wildcard"
                        ? `${theme.palette.primary.main}1A`
                        : theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.06)"
                          : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={1.25} mb={0.75}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      background: (theme) =>
                        currentUrlType === "wildcard"
                          ? theme.palette.primary.main
                          : theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: (theme) =>
                        currentUrlType === "wildcard"
                          ? theme.palette.common.white
                          : theme.palette.text.secondary,
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Star sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      color: (theme) => theme.palette.text.primary,
                      fontSize: "14px",
                    }}
                  >
                    {t("Wildcard Pattern")}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: "12px",
                    lineHeight: 1.5,
                    mb: 1.25,
                  }}
                >
                  {t("Use * to match any subdomain prefix")}
                </Typography>
                <Box
                  sx={{
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(0, 0, 0, 0.3)"
                        : "rgba(0, 0, 0, 0.05)",
                    borderRadius: "8px",
                    px: 1.25,
                    py: 1,
                    fontFamily: "monospace",
                    fontSize: "11px",
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.success.light
                        : theme.palette.success.dark,
                  }}
                >
                  *.walmart.com
                </Box>
              </Box>
            </Box>

            {/* Add URL Form */}
            <Box display="flex" gap={1.5} mb={2.5}>
              <TextField
                fullWidth
                placeholder={getPlaceholder()}
                value={urlInput}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") addUrl();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Public
                        sx={{
                          color: (theme) => theme.palette.text.disabled,
                          fontSize: 18,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.02)",
                    border: (theme) =>
                      theme.palette.mode === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(0, 0, 0, 0.1)",
                    borderRadius: "12px",
                    color: (theme) => theme.palette.text.primary,
                    fontSize: "13px",
                    minHeight: "40px",
                    "& input": {
                      padding: "8px 0",
                    },
                    "&:hover": {
                      background: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.04)",
                    },
                    "&.Mui-focused": {
                      background: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.04)",
                      borderColor: (theme) => theme.palette.primary.main,
                      boxShadow: (theme) =>
                        `0 0 0 4px ${theme.palette.primary.main}20`,
                    },
                    "& fieldset": {
                      border: "none",
                    },
                    "& input::placeholder": {
                      color: (theme) => theme.palette.text.disabled,
                      opacity: 1,
                      fontSize: "13px",
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                startIcon={
                  isCreating || isUpdating ? (
                    <CircularProgress size={16} sx={{ color: "inherit" }} />
                  ) : (
                    <Add sx={{ fontSize: 16 }} />
                  )
                }
                onClick={addUrl}
                disabled={!urlInput.trim() || isCreating || isUpdating}
                sx={{
                  background: (theme) => theme.palette.primary.main,
                  color: (theme) => theme.palette.common.white,
                  px: 2,
                  py: 1.25,
                  borderRadius: "12px",
                  fontSize: "13px",
                  fontWeight: 600,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  minWidth: "110px",
                  height: "40px",
                  boxShadow: (theme) =>
                    `0 4px 15px ${theme.palette.primary.main}40`,
                  "&:hover": {
                    background: (theme) => theme.palette.primary.dark,
                    transform: "translateY(-2px)",
                    boxShadow: (theme) =>
                      `0 6px 25px ${theme.palette.primary.main}60`,
                  },
                  "&:disabled": {
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.12)"
                        : "rgba(0, 0, 0, 0.12)",
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.3)"
                        : "rgba(0, 0, 0, 0.26)",
                    boxShadow: "none",
                    transform: "none",
                  },
                }}
              >
                {isCreating || isUpdating ? t("Adding...") : t("Add URL")}
              </Button>
            </Box>

            {/* URL List Header */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    fontSize: "14px",
                  }}
                >
                  {t("Whitelisted URLs")}
                </Typography>
                <Box
                  sx={{
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.08)",
                    px: 1.25,
                    py: 0.4,
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: (theme) => theme.palette.text.secondary,
                  }}
                >
                  {filteredUrls.length} {t("URLs")}
                </Box>
              </Box>

              {/* URL Filter Tabs */}
              <Box display="flex" gap={1}>
                <Chip
                  label={t("All")}
                  onClick={() => setUrlFilter("ALL")}
                  sx={{
                    background: (theme) =>
                      urlFilter === "ALL"
                        ? theme.palette.primary.main
                        : theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.08)",
                    color: (theme) =>
                      urlFilter === "ALL"
                        ? theme.palette.common.white
                        : theme.palette.text.primary,
                    fontWeight: urlFilter === "ALL" ? 600 : 500,
                    fontSize: "12px",
                    height: "28px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: (theme) =>
                        urlFilter === "ALL"
                          ? theme.palette.primary.dark
                          : theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.12)"
                            : "rgba(0, 0, 0, 0.12)",
                    },
                  }}
                />
                <Chip
                  label={t("Exact")}
                  onClick={() => setUrlFilter("EXACT")}
                  sx={{
                    background: (theme) =>
                      urlFilter === "EXACT"
                        ? theme.palette.info.main
                        : theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.08)",
                    color: (theme) =>
                      urlFilter === "EXACT"
                        ? theme.palette.common.white
                        : theme.palette.text.primary,
                    fontWeight: urlFilter === "EXACT" ? 600 : 500,
                    fontSize: "12px",
                    height: "28px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: (theme) =>
                        urlFilter === "EXACT"
                          ? theme.palette.info.dark
                          : theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.12)"
                            : "rgba(0, 0, 0, 0.12)",
                    },
                  }}
                />
                <Chip
                  label={t("Wildcard")}
                  onClick={() => setUrlFilter("WILDCARD")}
                  sx={{
                    background: (theme) =>
                      urlFilter === "WILDCARD"
                        ? theme.palette.warning.main
                        : theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.08)",
                    color: (theme) =>
                      urlFilter === "WILDCARD"
                        ? theme.palette.common.white
                        : theme.palette.text.primary,
                    fontWeight: urlFilter === "WILDCARD" ? 600 : 500,
                    fontSize: "12px",
                    height: "28px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: (theme) =>
                        urlFilter === "WILDCARD"
                          ? theme.palette.warning.dark
                          : theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.12)"
                            : "rgba(0, 0, 0, 0.12)",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* URL List */}
            <Box
              sx={{
                position: "relative",
                minHeight: filteredUrls?.length > 0 ? "auto" : "100px",
              }}
            >
              {(isCreating || isUpdating || deletingUrlId !== null) && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(0, 0, 0, 0.5)"
                        : "rgba(255, 255, 255, 0.7)",
                    backdropFilter: "blur(4px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "12px",
                    zIndex: 10,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <CircularProgress
                      size={32}
                      sx={{ color: (theme) => theme.palette.primary.main }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: (theme) => theme.palette.text.primary,
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    >
                      {deletingUrlId ? t("Deleting...") : t("Updating list...")}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box display="flex" flexDirection="column" gap={1}>
                {filteredUrls?.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      p: 1.25,
                      background: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "rgba(0, 0, 0, 0.02)",
                      border: (theme) =>
                        theme.palette.mode === "dark"
                          ? "1px solid rgba(255, 255, 255, 0.08)"
                          : "1px solid rgba(0, 0, 0, 0.08)",
                      borderRadius: "10px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.06)"
                            : "rgba(0, 0, 0, 0.04)",
                        borderColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.15)"
                            : "rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <Chip
                      label={item.type === "EXACT" ? t("Exact") : t("Wildcard")}
                      size="small"
                      sx={{
                        background: (theme) =>
                          item.type === "EXACT"
                            ? `${theme.palette.info.main}26`
                            : `${theme.palette.warning.main}26`,
                        color: (theme) =>
                          item.type === "EXACT"
                            ? theme.palette.info.main
                            : theme.palette.warning.main,
                        border: (theme) =>
                          item.type === "EXACT"
                            ? `1px solid ${theme.palette.info.main}4D`
                            : `1px solid ${theme.palette.warning.main}4D`,
                        fontWeight: 600,
                        fontSize: "10px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        height: "22px",
                      }}
                    />
                    <Typography
                      sx={{
                        flex: 1,
                        fontFamily: "monospace",
                        fontSize: "13px",
                        color: (theme) => theme.palette.text.primary,
                        wordBreak: "break-all",
                      }}
                    >
                      {item.url}
                    </Typography>
                    {item.description && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: (theme) => theme.palette.text.disabled,
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          ml: "auto",
                        }}
                      >
                        {item.description}
                      </Typography>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => removeUrl(item.id)}
                      disabled={deletingUrlId === item.id}
                      sx={{
                        width: 32,
                        height: 32,
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(239, 68, 68, 0.1)"
                            : "rgba(239, 68, 68, 0.08)",
                        border: (theme) =>
                          `1px solid ${theme.palette.error.main}40`,
                        borderRadius: "8px",
                        color: (theme) => theme.palette.error.main,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          background: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(239, 68, 68, 0.2)"
                              : "rgba(239, 68, 68, 0.15)",
                          borderColor: (theme) =>
                            `${theme.palette.error.main}60`,
                        },
                        "&:disabled": {
                          background: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.05)"
                              : "rgba(0, 0, 0, 0.05)",
                          color: (theme) => theme.palette.text.disabled,
                          borderColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.1)"
                              : "rgba(0, 0, 0, 0.1)",
                        },
                      }}
                    >
                      {deletingUrlId === item.id ? (
                        <CircularProgress
                          size={14}
                          sx={{ color: (theme) => theme.palette.error.main }}
                        />
                      ) : (
                        <Delete sx={{ fontSize: 14 }} />
                      )}
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Pattern Help */}
            <Box
              sx={{
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(59, 130, 246, 0.1)"
                    : "rgba(59, 130, 246, 0.05)",
                border: (theme) => `1px solid ${theme.palette.info.main}40`,
                borderRadius: "14px",
                p: 2,
                mt: 2.5,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <InfoOutlined
                  sx={{
                    fontSize: 18,
                    color: (theme) => theme.palette.info.main,
                  }}
                />
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    color: (theme) => theme.palette.info.main,
                    fontSize: "14px",
                  }}
                >
                  {t("Wildcard Pattern Examples")}
                </Typography>
              </Box>
              <Box
                display="grid"
                gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
                gap={2}
              >
                <Box
                  sx={{
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(0, 0, 0, 0.3)"
                        : "rgba(0, 0, 0, 0.05)",
                    borderRadius: "10px",
                    p: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "13px",
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.success.light
                          : theme.palette.success.dark,
                      mb: 1,
                    }}
                  >
                    *.walmart.com
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      color: (theme) => theme.palette.text.secondary,
                      fontSize: "12px",
                      lineHeight: 1.6,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        color: (theme) => theme.palette.text.primary,
                      }}
                    >
                      ✓ web.walmart.com
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        color: (theme) => theme.palette.text.primary,
                      }}
                    >
                      ✓ dev.web.walmart.com
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        color: (theme) => theme.palette.text.primary,
                      }}
                    >
                      ✓ api.dev.internal.walmart.com
                    </Box>
                  </Typography>
                </Box>
                <Box
                  sx={{
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(0, 0, 0, 0.3)"
                        : "rgba(0, 0, 0, 0.05)",
                    borderRadius: "10px",
                    p: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "13px",
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.success.light
                          : theme.palette.success.dark,
                      mb: 1,
                    }}
                  >
                    *.api.company.io
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      color: (theme) => theme.palette.text.secondary,
                      fontSize: "12px",
                      lineHeight: 1.6,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        color: (theme) => theme.palette.text.primary,
                      }}
                    >
                      ✓ v1.api.company.io
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        color: (theme) => theme.palette.text.primary,
                      }}
                    >
                      ✓ staging.v2.api.company.io
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        color: (theme) => theme.palette.text.primary,
                      }}
                    >
                      ✗ api.company.io (no subdomain)
                    </Box>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* General Settings Section */}
          {/* <Box
            sx={{
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.02)",
              backdropFilter: "blur(20px)",
              border: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)"
                }`,
              borderRadius: "20px",
              p: 2.5,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              mb={2.5}
              pb={2}
              sx={{
                borderBottom: (theme) =>
                  `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)"
                  }`,
              }}
            > */}
          {/* <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  background: (theme) => `${theme.palette.primary.main}26`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SettingsIcon
                  sx={{
                    fontSize: 22,
                    color: (theme) => theme.palette.primary.main,
                  }}
                />
              </Box> */}
          {/* <Box flex={1}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    mb: 0.5,
                  }}
                >
                  General Settings
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: "14px",
                  }}
                >
                  Configure application preferences
                </Typography>
              </Box> */}
          {/* </Box> */}

          {/* <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 1.5,
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.03)"
                    : "rgba(0, 0, 0, 0.02)",
                border: (theme) =>
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.08)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                borderRadius: "12px",
                mb: 1,
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.06)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Box flex={1}>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    mb: 0.5,
                  }}
                >
                  Dark Mode
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: "13px",
                  }}
                >
                  Use dark theme across the app
                </Typography>
              </Box>
              {savingTheme && (
                <CircularProgress
                  size={20}
                  sx={{ color: (theme) => theme.palette.primary.main, mr: 1 }}
                />
              )}
              <Switch
                checked={theme === "dark"}
                disabled={savingTheme}
                onChange={async (e) => {
                  const newTheme = e.target.checked ? "dark" : "light";
                  setTheme(newTheme);
                  setSavingTheme(true);

                  await handleSaveSettings(
                    undefined,
                    newTheme,
                    undefined,
                    undefined,
                  );
                  setSavingTheme(false);
                }}
                sx={{
                  width: 52,
                  height: 28,
                  padding: 0,
                  "& .MuiSwitch-switchBase": {
                    padding: 0,
                    margin: "3px",
                    transitionDuration: "300ms",
                    "&.Mui-checked": {
                      transform: "translateX(24px)",
                      color: (theme) => theme.palette.common.white,
                      "& + .MuiSwitch-track": {
                        background: (theme) => theme.palette.primary.main,
                        opacity: 1,
                        border: 0,
                      },
                    },
                  },
                  "& .MuiSwitch-thumb": {
                    width: 22,
                    height: 22,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                  },
                  "& .MuiSwitch-track": {
                    borderRadius: "14px",
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.2)",
                    opacity: 1,
                  },
                }}
              />
            </Box> */}

          {/* <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 1.5,
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.03)"
                    : "rgba(0, 0, 0, 0.02)",
                border: (theme) =>
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.08)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                borderRadius: "12px",
                mb: 1,
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.06)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Box flex={1}>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    mb: 0.5,
                  }}
                >
                  Email Notifications
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: "13px",
                  }}
                >
                  Get notified about project updates
                </Typography>
              </Box>
              {savingEmailNotif && (
                <CircularProgress
                  size={20}
                  sx={{ color: (theme) => theme.palette.primary.main, mr: 1 }}
                />
              )}
              <Switch
                checked={emailNotifications}
                disabled={savingEmailNotif}
                onChange={async (e) => {
                  const newEmailNotif = e.target.checked;
                  setEmailNotifications(newEmailNotif);
                  setSavingEmailNotif(true);

                  await handleSaveSettings(
                    undefined,
                    undefined,
                    newEmailNotif,
                    undefined,
                  );
                  setSavingEmailNotif(false);
                }}
                sx={{
                  width: 52,
                  height: 28,
                  padding: 0,
                  "& .MuiSwitch-switchBase": {
                    padding: 0,
                    margin: "3px",
                    transitionDuration: "300ms",
                    "&.Mui-checked": {
                      transform: "translateX(24px)",
                      color: (theme) => theme.palette.common.white,
                      "& + .MuiSwitch-track": {
                        background: (theme) => theme.palette.primary.main,
                        opacity: 1,
                        border: 0,
                      },
                    },
                  },
                  "& .MuiSwitch-thumb": {
                    width: 22,
                    height: 22,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                  },
                  "& .MuiSwitch-track": {
                    borderRadius: "14px",
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.2)",
                    opacity: 1,
                  },
                }}
              />
            </Box> */}

          {/* Compact View */}
          {/* <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 2,
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.03)"
                    : "rgba(0, 0, 0, 0.02)",
                border: (theme) =>
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.08)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.06)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Box flex={1}>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    mb: 0.5,
                  }}
                >
                  Compact View
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: "13px",
                  }}
                >
                  Show more projects per page
                </Typography>
              </Box>
              <Switch
                checked={compactView}
                onChange={(e) => setCompactView(e.target.checked)}
                sx={{
                  width: 52,
                  height: 28,
                  padding: 0,
                  "& .MuiSwitch-switchBase": {
                    padding: 0,
                    margin: "3px",
                    transitionDuration: "300ms",
                    "&.Mui-checked": {
                      transform: "translateX(24px)",
                      color: (theme) => theme.palette.common.white,
                      "& + .MuiSwitch-track": {
                        background: (theme) => theme.palette.primary.main,
                        opacity: 1,
                        border: 0,
                      },
                    },
                  },
                  "& .MuiSwitch-thumb": {
                    width: 22,
                    height: 22,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                  },
                  "& .MuiSwitch-track": {
                    borderRadius: "14px",
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.2)",
                    opacity: 1,
                  },
                }}
              />
            </Box> */}
          {/* </Box> */}
        </Container>
      </BaseScrollableContainer>
    </TaskLayout>
  );
}
